// src/components/tools/management/IcebergModelTool.jsx
// Iceberg Model Tool for Systemic Issue Analysis
import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Plus, Edit2, Trash2, Save, Loader2, Target, Zap, ChevronUp, 
  Layers, Users, MessageSquare, RefreshCw, X, Check, Eye, ChevronRight,
  ChevronDown, AlertTriangle, Lightbulb, TrendingDown, Clock, CheckCircle
} from 'lucide-react';
// Import toolsService from a relative path if needed, but for the provided context,
// we rely on it being passed as a prop from ToolsView.
// Note: We use the toolService prop instead of importing it here.

// ============================================================
// CONSTANTS & CONFIGURATIONS
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  WIZARD: 'WIZARD',
  MODEL_VIEW: 'MODEL_VIEW'
};

// Static color classes grouped for guaranteed Tailwind compilation
const COLOR_CONFIG = {
    red:    { border: 'border-red-500', textIcon: 'text-red-600', textHeader: 'text-red-800', bgLight: 'bg-red-50', textBadge: 'text-red-700' },
    amber:  { border: 'border-amber-500', textIcon: 'text-amber-600', textHeader: 'text-amber-800', bgLight: 'bg-amber-50', textBadge: 'text-amber-700' },
    blue:   { border: 'border-blue-500', textIcon: 'text-blue-600', textHeader: 'text-blue-800', bgLight: 'bg-blue-50', textBadge: 'text-blue-700' },
    indigo: { border: 'border-indigo-500', textIcon: 'text-indigo-600', textHeader: 'text-indigo-800', bgLight: 'bg-indigo-50', textBadge: 'text-indigo-700' },
    emerald: { border: 'border-emerald-500', textIcon: 'text-emerald-600', textHeader: 'text-emerald-800', bgLight: 'bg-emerald-50', textBadge: 'text-emerald-700' }
};

const IcebergLevel = {
  EVENT: { id: 'event', label: 'Event', icon: AlertTriangle, colorKey: 'red', description: 'What happened? The visible, immediate occurrences.' },
  PATTERN: { id: 'pattern', label: 'Pattern', icon: TrendingDown, colorKey: 'amber', description: 'What trends or sequences of events have been recurring over time?' },
  STRUCTURE: { id: 'structure', label: 'Systemic Structure', icon: Layers, colorKey: 'blue', description: 'What underlying systems, processes, or policies create the patterns?' },
  MENTAL_MODEL: { id: 'mental_model', label: 'Mental Model', icon: Lightbulb, colorKey: 'indigo', description: 'What beliefs, values, and assumptions are held by stakeholders that reinforce the structure?' }
};

const WIZARD_STEPS = [
  IcebergLevel.EVENT,
  IcebergLevel.PATTERN,
  IcebergLevel.STRUCTURE,
  IcebergLevel.MENTAL_MODEL
];

// Correctly defined using the MENTAL_MODEL key
const EXAMPLE_CONTENT = {
    [IcebergLevel.EVENT.id]: 'A key employee resigned unexpectedly last week. (E.g., A key employee resigned unexpectedly last week)',
    [IcebergLevel.PATTERN.id]: 'We have seen an increase in sick days and voluntary departures over the last six months. (E.g., We have seen an increase in sick days and voluntary departures over the last six months)',
    [IcebergLevel.STRUCTURE.id]: 'A policy of chronic understaffing and a lack of formalized growth paths leads to employee burnout. (E.g., A policy of chronic understaffing and a lack of formalized growth paths leads to employee burnout)',
    [IcebergLevel.MENTAL_MODEL.id]: 'The belief that "Our people should be grateful for the job, so we don’t need to invest heavily in retention" or "Staff are a cost center, not an asset." (E.g., The belief that "Our people should be grateful for the job, so we don’t need to invest heavily in retention")'
}

// ============================================================
// UI COMPONENTS
// ============================================================

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-white rounded-2xl border border-gray-100">
    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

// ============================================================
// WIZARD STEPPER
// ============================================================

const IcebergWizardStep = ({ step, data, update }) => {
    const Icon = step.icon;
    const config = COLOR_CONFIG[step.colorKey]; // Use static color config
    const isMentalModel = step.id === IcebergLevel.MENTAL_MODEL.id;

    const currentAnalysis = data.analysis?.[step.id] || { title: '', notes: '' };
    
    // Auto-fill title from notes if not manually set
    const autoTitle = currentAnalysis.notes.split('\n')[0].substring(0, 50) || `Untitled ${step.label}`;

    // Get context from previous levels (for display only)
    const context = WIZARD_STEPS
        .filter(s => s.id !== step.id && s.id !== IcebergLevel.MENTAL_MODEL.id)
        .map(s => {
            const c = data.analysis?.[s.id];
            if (!c?.notes) return null;
            return {
                label: s.label,
                title: c.title || c.notes.split('\n')[0].substring(0, 50),
                notes: c.notes.replace(/\n/g, ' ')
            };
        })
        .filter(c => c !== null);


    const handleUpdate = (field, value) => {
        const isUpdatingTitle = field === 'title';
        const isUpdatingNotes = field === 'notes';
        
        // 1. Start with the existing analysis data
        let updatedSection = { ...currentAnalysis, [field]: value };

        // 2. Adjust title if updating notes AND title is currently empty
        if (isUpdatingNotes && !currentAnalysis.title.trim()) {
            updatedSection.title = value.split('\n')[0].substring(0, 50);
        }

        // 3. If the user explicitly sets the title, we must respect it directly.
        if (isUpdatingTitle) {
            updatedSection.title = value;
        }

        // 4. Update the parent state
        const newAnalysis = { 
            ...data.analysis, 
            [step.id]: updatedSection
        };
        update('analysis', newAnalysis);
    };

    const promptText = EXAMPLE_CONTENT[step.id];

    return (
        <div className="space-y-6">
            {/* Step Header (Using static class lookups) */}
            <div className={`p-4 rounded-xl bg-white border-l-4 ${config.border} shadow-sm`}>
                <div className="flex items-center gap-3">
                    <Icon className={`w-6 h-6 ${config.textIcon}`} />
                    <div>
                        <h2 className={`font-bold text-lg ${config.textHeader}`}>{step.label}</h2>
                        <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                </div>
            </div>

            {/* Title Input (always visible for manual override) */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Title (Quick Reference)</label>
                <input
                    type="text"
                    // Use the autoTitle as the placeholder, but the current value as the value
                    placeholder={autoTitle}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl font-semibold text-gray-800"
                    value={currentAnalysis.title} // <-- Bound to the actual title state
                    onChange={(e) => handleUpdate('title', e.target.value)}
                    // Removed 'required' attribute, as it was only for the top level (Event)
                />
            </div>
            
            {/* Context from Previous Levels */}
            {context.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                    <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-500" /> Context from Deeper Levels
                    </h3>
                    <div className="space-y-1">
                        {context.map((c, i) => (
                            <div key={i} className="text-xs text-gray-600">
                                <span className="font-semibold">{c.label}:</span> {c.notes}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Notes/Content Input */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{step.label} Analysis Notes *</label>
                <textarea
                    rows={isMentalModel ? 6 : 4}
                    placeholder={promptText}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                    value={currentAnalysis.notes}
                    onChange={(e) => handleUpdate('notes', e.target.value)}
                    required
                />
            </div>

            {isMentalModel && (
                <div className="p-4 bg-indigo-50 rounded-xl text-sm text-indigo-800 flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>
                        The **Mental Model** is the deepest layer. It often explains why the Systemic Structure persists. Addressing an issue at this level leads to the most fundamental and lasting change.
                    </span>
                </div>
            )}
        </div>
    );
};

// ============================================================
// WIZARD
// ============================================================

const IcebergWizard = ({ item, onSave, onCancel, saving }) => {
    const [step, setStep] = useState(0);
    const [data, setData] = useState(item || {
        id: undefined,
        name: 'New Iceberg Analysis',
        status: 'draft', // draft, complete
        analysis: {},
        createdAt: new Date().toISOString()
    });

    const currentLevel = WIZARD_STEPS[step];
    const isLastStep = step === WIZARD_STEPS.length - 1;

    const handleUpdate = (field, value) => {
        setData((prev) => ({ ...prev, [field]: value }));
    };

    const handleNext = () => {
        if (!data.analysis?.[currentLevel.id]?.notes) return;
        if (isLastStep) return;
        setStep(s => s + 1);
    };

    const handleBack = () => {
        if (step === 0) return;
        setStep(s => s - 1);
    };
    
    const handleFinalSave = () => {
        if (!data.analysis?.[currentLevel.id]?.notes) {
             alert("Please complete the Mental Model analysis before saving.");
             return;
        }
        
        // Finalize document name using the event title if available
        const finalName = data.analysis?.event?.title || data.analysis?.event?.notes.split('\n')[0].substring(0, 50) || 'Untitled Iceberg Analysis';

        const dataToSave = {
            ...data,
            name: finalName,
            status: 'complete',
            updatedAt: new Date().toISOString()
        };
        onSave(dataToSave);
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Iceberg Model Analysis</h1>
                            <p className="text-sm text-gray-500">Uncovering root causes of organizational issues</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="max-w-4xl mx-auto p-6 space-y-6">
                    
                    {/* Stepper */}
                    <div className="flex items-center gap-1 overflow-x-auto pb-2 bg-white rounded-xl p-4 border border-gray-200">
                        {WIZARD_STEPS.map((level, index) => {
                            const Icon = level.icon;
                            const isActive = index === step;
                            const isCompleted = index < step || data.analysis?.[level.id]?.notes;
                            const config = COLOR_CONFIG[level.colorKey];

                            return (
                                <React.Fragment key={level.id}>
                                    <button
                                        onClick={() => setStep(index)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all flex-shrink-0 ${
                                            isActive 
                                                ? 'bg-indigo-600 text-white shadow-md' 
                                                : isCompleted
                                                    ? `${COLOR_CONFIG.emerald.bgLight} ${COLOR_CONFIG.emerald.textBadge}`
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {isActive && <Eye className="w-4 h-4" />}
                                        {isCompleted && !isActive && <Check className="w-4 h-4" />}
                                        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : isCompleted ? COLOR_CONFIG.emerald.textBadge : 'text-gray-600'}`} />
                                        {level.label}
                                    </button>
                                    {index < WIZARD_STEPS.length - 1 && (
                                        <ChevronRight className={`w-5 h-5 ${index < step ? 'text-indigo-400' : 'text-gray-300'}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Step Content */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 min-h-[400px]">
                        <IcebergWizardStep 
                            step={currentLevel} 
                            data={data} 
                            update={handleUpdate}
                        />
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <button
                            onClick={handleBack}
                            disabled={step === 0}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl disabled:opacity-50 flex items-center gap-2"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Previous
                        </button>

                        {isLastStep ? (
                            <button
                                onClick={handleFinalSave}
                                disabled={saving || !data.analysis?.[currentLevel.id]?.notes}
                                className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save Analysis
                            </button>
                        ) : (
                            <button
                                onClick={handleNext}
                                disabled={!data.analysis?.[currentLevel.id]?.notes}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// MODEL VIEW
// ============================================================

const IcebergModelView = ({ item, onEdit, onBack, onDelete }) => {
    const analysis = item.analysis || {};
    const [showPrimer, setShowPrimer] = useState(false);
    
    const levels = [
        IcebergLevel.MENTAL_MODEL,
        IcebergLevel.STRUCTURE,
        IcebergLevel.PATTERN,
        IcebergLevel.EVENT
    ];

    const ModelLevel = ({ level }) => {
        const data = analysis[level.id];
        const Icon = level.icon;
        const config = COLOR_CONFIG[level.colorKey];

        if (!data?.notes) return null;

        return (
            <div className={`p-5 rounded-2xl bg-white border-l-4 ${config.border} shadow-lg mb-4`}>
                <div className="flex items-center gap-3 mb-2">
                    <Icon className={`w-6 h-6 ${config.textIcon} flex-shrink-0`} />
                    <h3 className={`font-bold text-lg ${config.textHeader}`}>{level.label}: {data.title || 'Analysis'}</h3>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {data.notes}
                </div>
            </div>
        );
    };

    return (
        <div className="h-full overflow-y-auto bg-gray-50">
            <div className="max-w-4xl mx-auto p-6 space-y-6">
                
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{item.name || 'Untitled Analysis'}</h1>
                            <p className="text-gray-500">Root Cause Analysis using the Iceberg Model</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onEdit(item)}
                            className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl flex items-center gap-2"
                        >
                            <Edit2 className="w-4 h-4" /> Edit
                        </button>
                        <button
                            onClick={() => { if(window.confirm('Delete this analysis?')) onDelete(item.id); }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-xl"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Primer */}
                <div className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer" onClick={() => setShowPrimer(!showPrimer)}>
                    <div className="flex items-center justify-between">
                        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Layers className="w-5 h-5 text-gray-600" /> What is the Iceberg Model?
                        </h2>
                        {showPrimer ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                    {showPrimer && (
                        <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-700 space-y-2">
                            <p>The **Iceberg Model** is a systems thinking framework that encourages managers to look beyond **Events** (the tip of the iceberg) to identify the underlying causes of an issue. Only by addressing the deepest layers (**Mental Models**) can sustainable change be achieved. 

[Image of the Iceberg Model illustrating the four levels: Event (tip of water), Pattern, Structure, Mental Model (deepest base)]
</p>
                            <div className="grid grid-cols-2 gap-4 pt-2">
                                <div className="space-y-1">
                                    <p className="font-medium text-gray-900 flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-red-500" /> Events (10% Visible)</p>
                                    <p className="text-xs text-gray-600">Discrete, observable occurrences (e.g., "The server crashed").</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="font-medium text-gray-900 flex items-center gap-1"><TrendingDown className="w-4 h-4 text-amber-500" /> Patterns (Invisible)</p>
                                    <p className="text-xs text-gray-600">Trends and recurring sequences (e.g., "Crashes are increasing").</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="font-medium text-gray-900 flex items-center gap-1"><Layers className="w-4 h-4 text-blue-500" /> Structure (Invisible)</p>
                                    <p className="text-xs text-gray-600">The underlying systems and processes (e.g., "No budget for hardware upgrades").</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="font-medium text-gray-900 flex items-center gap-1"><Lightbulb className="w-4 h-4 text-indigo-500" /> Mental Models (Deepest)</p>
                                    <p className="text-xs text-gray-600">Deeply held beliefs that justify the structure (e.g., "IT is just a cost center").</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Analysis Levels (Reverse Order for Visual Iceberg) */}
                <h2 className="text-xl font-bold text-gray-900 pt-4">Analysis Breakdown</h2>
                
                {levels.map(level => <ModelLevel key={level.id} level={level} />)}
            </div>
        </div>
    );
};


// ============================================================
// MAIN COMPONENT (IcebergModelTool)
// ============================================================

const IcebergModelTool = ({ bookId, customStyling = {}, toolsService, onBack }) => {
    const toolSlug = 'iceberg-analysis';
    const [view, setView] = useState(ViewState.DASHBOARD);
    const [loading, setLoading] = useState(true);
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);
    const [saving, setSaving] = useState(false);

    // Load Data
    useEffect(() => {
        loadData();
    }, [bookId, toolsService]); // Added toolsService to dependency array

    const loadData = async () => {
        setLoading(true);
        try {
            // Ensure toolSlug matches the one used in the tool catalog
            const data = await toolsService.getToolData(bookId, toolSlug); 
            setModels(data || []);
        } catch (err) {
            console.error("Error loading iceberg data:", err);
        } finally {
            setLoading(false);
        }
    };

    // Save Data
    const handleSave = async (item) => {
        setSaving(true);
        try {
            // Ensure toolSlug matches the one used in the tool catalog
            const saved = await toolsService.saveToolData(bookId, toolSlug, item);
            setModels((prev) => {
                const exists = prev.some((i) => i.id === saved.id);
                return exists ? prev.map((i) => (i.id === saved.id ? saved : i)) : [saved, ...prev];
            });
            setSelectedModel(saved);
            setView(ViewState.MODEL_VIEW);
        } catch (err) {
            alert("Failed to save Iceberg Analysis.");
        } finally {
            setSaving(false);
        }
    };

    // Delete Data
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this analysis?")) return;
        try {
            // Ensure toolSlug matches the one used in the tool catalog
            await toolsService.deleteToolData(bookId, toolSlug, id);
            setModels((prev) => prev.filter((i) => i.id !== id));
            setView(ViewState.DASHBOARD);
        } catch (err) {
            console.error("Error deleting item:", err);
        }
    };

    // --- DASHBOARD VIEW ---
    const Dashboard = () => {
        const completedCount = models.filter(m => m.status === 'complete').length;

        return (
            <div className="h-full overflow-y-auto">
                <div className="max-w-6xl mx-auto p-6 space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                                <ArrowLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Iceberg Model Tool</h1>
                                <p className="text-gray-500">Analyse root causes using Systems Thinking</p>
                            </div>
                        </div>
                        <button
                            onClick={() => { setSelectedModel(null); setView(ViewState.WIZARD); }}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 shadow-sm transition-all"
                        >
                            <Plus className="w-4 h-4" /> New Analysis
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <StatCard icon={Layers} label="Total Analyses" value={models.length} color="indigo" onClick={() => models.length > 0 && setView(ViewState.DASHBOARD)} />
                        <StatCard icon={CheckCircle} label="Completed" value={completedCount} color="emerald" onClick={() => models.length > 0 && setView(ViewState.DASHBOARD)} />
                        <StatCard icon={Lightbulb} label="Mental Models Identified" value={completedCount} subValue="Deepest level insights" color="purple" onClick={() => models.length > 0 && setView(ViewState.DASHBOARD)} />
                    </div>

                    {/* List */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-6">
                        <h2 className="font-semibold text-gray-900 mb-4">Saved Analyses</h2>
                        {loading ? (
                            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
                        ) : models.length === 0 ? (
                            <EmptyState 
                                icon={Layers} 
                                title="No analyses saved yet" 
                                description="Start a new analysis to drill down into the root causes of an issue."
                                action={
                                    <button onClick={() => { setSelectedModel(null); setView(ViewState.WIZARD); }} className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 mt-4">
                                        <Plus className="w-5 h-5" /> New Analysis
                                    </button>
                                }
                            />
                        ) : (
                            <div className="space-y-3">
                                {models.map(model => (
                                    <button 
                                        key={model.id}
                                        onClick={() => { setSelectedModel(model); setView(ViewState.MODEL_VIEW); }}
                                        className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-left group"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors truncate">{model.name}</h3>
                                            <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                                {model.status === 'complete' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                                                {model.status === 'complete' ? 'Completed' : 'Draft'}
                                            </p>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-gray-300" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // --- RENDER SWITCH ---
    switch (view) {
        case ViewState.WIZARD:
            return <IcebergWizard 
                item={selectedModel} 
                onSave={handleSave} 
                onCancel={() => setView(ViewState.DASHBOARD)} 
                saving={saving}
            />;
        case ViewState.MODEL_VIEW:
            return <IcebergModelView 
                item={selectedModel} 
                onEdit={m => { setSelectedModel(m); setView(ViewState.WIZARD); }} 
                onBack={() => setView(ViewState.DASHBOARD)} 
                onDelete={handleDelete}
            />;
        default:
            return <Dashboard />;
    }
};

export default IcebergModelTool;